import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, yieldToMainThread, parseIntOrDefault } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';
import { resolveVariantId } from '@theme/variant-resolution';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** @type {Promise<void>} */
  #animationPromise = Promise.resolve();

  #animationGeneration = 0;
  #cancelAnimationWait;
  #feedbackButton;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    this.resetFeedback();
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = Boolean(this.closest('product-form-component')?.isCartMutationUncertain);
  }

  /**
   * Clears stale success feedback when the selected variant changes.
   * Variant selection owns the new purchase state; old add feedback must not mask it.
   */
  resetFeedback() {
    this.#animationGeneration += 1;
    this.#cancelAnimationWait?.();
    this.#cancelAnimationWait = undefined;
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];
    this.refs.addToCartButton.removeAttribute('data-added');
    this.#feedbackButton?.removeAttribute('data-added');
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    if (!this.canStartFeedback()) return;
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
      productForm?.prepareAddToCartFeedback(this.refs.addToCartButton);
    }
  }

  /** Shared validation for the main button and its sticky presentation consumer. */
  canStartFeedback() {
    const form = this.closest('form');
    if (!form?.checkValidity()) return false;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    if (productForm?.isAddToCartPending || productForm?.isCartMutationUncertain) return false;
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return false;
      }
    }
    return true;
  }

  /**
   * Starts the existing add-to-cart micro-animation and exposes the same promise
   * to cart-surface consumers so transitions can be sequenced without fixed delays.
   *
   * @returns {Promise<void>}
   */
  animateAddToCart = function (button = this.refs.addToCartButton) {
    this.resetFeedback();
    this.#feedbackButton = button;
    this.#animationPromise = this.#runAddToCartAnimation(button);
    return this.#animationPromise;
  };

  /**
   * Resolves when the active add-to-cart micro-animation has visually completed.
   *
   * @returns {Promise<void>}
   */
  whenAddToCartAnimationSettled() {
    return this.#animationPromise;
  }

  async #runAddToCartAnimation(addToCartButton) {
    this.#cancelAnimationWait?.();
    const generation = ++this.#animationGeneration;
    let cancelWait;
    const cancelled = new Promise((resolve) => { cancelWait = resolve; });
    this.#cancelAnimationWait = cancelWait;
    const watchdog = setTimeout(cancelWait, 2000);

    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // Read the real Web Animations timeline instead of coupling the drawer
    // to a duplicated millisecond duration.
    try {
      await Promise.race([
        yieldToMainThread().then(() => {
          if (generation !== this.#animationGeneration || !this.isConnected || !addToCartButton.isConnected) return;
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
          return onAnimationEnd(addToCartButton);
        }),
        cancelled,
      ]);
    } catch {
      // Visual feedback must never reject a successful cart update.
    } finally {
      clearTimeout(watchdog);
      if (this.#cancelAnimationWait === cancelWait) this.#cancelAnimationWait = undefined;
    }
    if (generation !== this.#animationGeneration || !this.isConnected || !addToCartButton.isConnected) return;

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} cartRequestRecovery - The localized cart verification fallback.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @typedef {object} QueuedAddToCartItem
 * @property {number} quantity - The quantity captured when Add was clicked.
 * @property {number} generation - The variant-change generation active when Add was clicked.
 * @property {string | null} intendedVariantId - The selected option's data-variant-id, when present.
 * @property {boolean | undefined} intendedAvailable - Availability captured from the selected option when authoritative.
 * @property {Promise<unknown> | null} pendingVariantChange - The server-side section fetch for the clicked selection.
 * @property {string | null} variantResolutionUrl - A section-rendering URL used only when server resolution is still needed.
 *
 * @typedef {object} QuantityConstraints
 * @property {string} min
 * @property {string | null} max
 * @property {string} step
 * @property {string | null} cartQuantity
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #runtimeReady = false;

  /** @type {number | undefined} */
  #timeout;

  #activeCartAdds = 0;
  #queuedCartDrain = false;
  #cartMutationUncertain = false;
  #preparedFeedback = null;
  #activeFeedback = null;

  get isCartMutationUncertain() {
    return this.#cartMutationUncertain;
  }

  get isAddToCartPending() {
    return this.#activeCartAdds > 0 || this.#queuedCartDrain || this.#addToCartQueue.length > 0;
  }

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /**
   * Adds queued while a variant change is in flight. Each entry captures the selection state and
   * generation active when Add was clicked, then resolves that selection at drain time.
   * @type {QueuedAddToCartItem[]}
   */
  #addToCartQueue = [];

  /**
   * The in-flight variant-change section fetch promise. The queue drain awaits this before
   * reading the resolved variant id.
   * @type {Promise<unknown> | null}
   */
  #pendingVariantChange = null;

  /**
   * Authoritative synchronous state emitted by the variant picker for direct one-option selections.
   * The product form consumes this state directly instead of re-reading picker DOM on the ATC hot path.
   * @type {{variantId: string, available: boolean} | null}
   */
  #localVariantState = null;

  connectedCallback() {
    this.#runtimeReady = false;
    this.removeAttribute('data-tfp-product-form-ready');
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Only subscribe to global cart updates when this form actually renders
    // quantity/cart-state UI that needs synchronization.
    const needsCartQuantitySync = Boolean(
      this.refs.quantitySelector || this.refs.quantityLabelCartCount || this.refs.quantityRules || this.refs.volumePricing
    );
    if (needsCartQuantitySync) {
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    }
    this.#runtimeReady = true;
    this.setAttribute('data-tfp-product-form-ready', '');
    this.#syncCartPendingState();
    this.#syncUncertainCartState();
  }

  updatedCallback() {
    this.removeAttribute('data-tfp-product-form-ready');
    super.updatedCallback();
    if (this.isConnected && this.#runtimeReady) this.setAttribute('data-tfp-product-form-ready', '');
    this.#syncCartPendingState();
    this.#syncUncertainCartState();
  }

  disconnectedCallback() {
    this.#runtimeReady = false;
    this.removeAttribute('data-tfp-product-form-ready');
    this.#settleFeedback(this.#preparedFeedback);
    this.#settleFeedback(this.#activeFeedback);
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Exposes the active primary ATC animation to cart-surface orchestration.
   * The product form remains the owner of which add-to-cart control is primary.
   *
   * @returns {Promise<void>}
   */
  whenAddToCartAnimationSettled() {
    if (this.#activeFeedback) return this.#activeFeedback.promise;
    if (this.#preparedFeedback) return this.#preparedFeedback.promise;
    return this.refs.addToCartButtonContainer?.whenAddToCartAnimationSettled?.() ?? Promise.resolve();
  }

  prepareAddToCartFeedback(button) {
    if (!this.refs.addToCartButtonContainer?.canStartFeedback()) return false;
    this.refs.addToCartButtonContainer.resetFeedback();
    this.#settleFeedback(this.#preparedFeedback);
    this.#settleFeedback(this.#activeFeedback);
    let resolve;
    const promise = new Promise((settle) => {
      resolve = settle;
    });
    this.#preparedFeedback = { button, promise, resolve, settled: false, animationStarted: false };
    return true;
  }

  /**
   * Adds authoritative variants through the canonical product-form cart pipeline.
   * External consumers such as OOS Rescue must use this instead of owning cart/add.js.
   *
   * @param {Array<{variantId: string | number, quantity?: number}>} items
   * @param {{ productId?: string | number }} [metadata]
   * @returns {Promise<unknown>}
   */
  addItems(items, metadata = {}) {
    const normalizedItems = items
      .map((item) => ({
        variantId: String(item.variantId || ''),
        quantity: Math.max(1, Number(item.quantity) || 1),
      }))
      .filter((item) => item.variantId);

    return this.#processBatchAddToCart(normalizedItems, metadata);
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  /**
   * Projects an authoritative local one-option variant into the primary purchase CTA.
   * The server-rendered section response remains canonical and reconciles this shortly after.
   * @param {boolean} available
   */
  #applyLocalPurchasePresentation(available) {
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!button) return;

    const label = available ? this.dataset.addToCartLabel : this.dataset.soldOutLabel;
    const labelElement = button.querySelector('[data-add-to-cart-label]');
    if (label && labelElement) labelElement.textContent = label;

    const icon = button.querySelector('.add-to-cart-icon');
    if (icon instanceof HTMLElement) icon.hidden = !available;
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    // Fallback for pages without cart-items-component (e.g. page-based cart on product pages)
    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Returns the server-rendered sections that should travel with a cart mutation.
   * Cart content remains owned by cart-items; cart presentation is bundled so the
   * drawer can update free-shipping/payment messaging without a follow-up request.
   *
   * @returns {string[]}
   */
  #getCartSectionIds() {
    const sectionIds = new Set();

    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    }

    if (document.querySelector('cart-drawer-component[auto-open]')) {
      sectionIds.add('cart-drawer-section');
    }

    const presentationMarker = document.querySelector('[data-cart-free-shipping-success-configured]');
    const presentationSection = presentationMarker?.closest('.shopify-section');
    const presentationSectionId = presentationSection?.id.replace(/^shopify-section-/, '');
    if (presentationSectionId) sectionIds.add(presentationSectionId);

    return [...sectionIds];
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;
    if (!(this.refs.quantitySelector || this.refs.quantityLabelCartCount || this.refs.quantityRules || this.refs.volumePricing)) {
      return;
    }

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          return this.#refreshCart().then((cart) => {
            if (this.isConnected) this.#updateCartQuantity(cart);
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();
    if (this.#cartMutationUncertain) {
      this.#syncUncertainCartState();
      return;
    }
    if (this.isAddToCartPending) return;

    if (this.#variantChangeInProgress) {
      const queuedItem = this.#createQueuedAddToCartItem();

      // For a direct one-option PDP selection, the picker already owns an authoritative
      // variant id + availability. Start the cart mutation immediately and let the
      // existing section request reconcile presentation in the background.
      if (this.#canFastAddDuringVariantChange(queuedItem)) {
        this.#processAddToCart(queuedItem.intendedVariantId || undefined, queuedItem.quantity, event);
        return;
      }

      this.#addToCartQueue.push(queuedItem);
      this.#syncCartPendingState();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {boolean}
   */
  #canFastAddDuringVariantChange(item) {
    const hasQuantitySensitiveUi = Boolean(
      this.refs.quantitySelector ||
        this.refs.quantitySelectorWrapper ||
        this.refs.quantityRules ||
        this.refs.quantityLabel ||
        this.refs.quantityLabelCartCount ||
        this.refs.pricePerItem ||
        this.refs.volumePricing
    );

    return Boolean(
      item.intendedVariantId &&
        item.intendedAvailable === true &&
        this.#getVariantIdInput() === item.intendedVariantId &&
        !hasQuantitySensitiveUi
    );
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /** @returns {QueuedAddToCartItem} */
  #createQueuedAddToCartItem() {
    const quantity = this.#getQuantity();

    if (this.#localVariantState) {
      return {
        quantity,
        generation: this.#variantChangeGeneration,
        intendedVariantId: this.#localVariantState.variantId,
        intendedAvailable: this.#localVariantState.available,
        pendingVariantChange: this.#pendingVariantChange,
        variantResolutionUrl: null,
      };
    }

    const picker = this.#getVariantPicker();
    const selectedOption = picker?.selectedOption;

    return {
      quantity,
      generation: this.#variantChangeGeneration,
      intendedVariantId: selectedOption?.dataset.variantId ?? null,
      intendedAvailable: undefined,
      pendingVariantChange: this.#pendingVariantChange,
      variantResolutionUrl: picker && selectedOption ? picker.buildRequestUrl(selectedOption) : null,
    };
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const feedback = this.#activatePreparedFeedback();
    if (this.#cartMutationUncertain) {
      this.#settleFeedback(feedback);
      this.#syncUncertainCartState();
      return;
    }
    const { addToCartTextError } = this.refs;
    this.refs.cartRequestRecovery?.classList.add('hidden');
    let cartMutationConfirmed = false;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) {
        this.#settleFeedback(feedback);
        return;
      }
    }

    const form = this.querySelector('form');
    if (!form) {
      this.#settleFeedback(feedback);
      throw new Error('Product form element missing');
    }

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        this.#settleFeedback(feedback);
        return;
      }
    }

    let formData;
    try {
      formData = new FormData(form);
    } catch (error) {
      this.#settleFeedback(feedback);
      throw error;
    }

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartSectionIds = this.#getCartSectionIds();
    if (cartSectionIds.length > 0) {
      formData.set('sections', cartSectionIds.join(','));
    }

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const feedbackProductId = String(this.dataset.productId || '');
    const feedbackVariantId = String(formData.get('id') || '');
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.#activeCartAdds += 1;
    this.#syncCartPendingState();
    deferredEventPromise.promise.catch(() => {
      for (const container of allAddToCartContainers) container.resetFeedback();
    });

    const cartUpdateEvent = new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [
        {
          merchandiseId: /** @type {string} */ (formData.get('id')),
          quantity: itemCount,
        },
      ],
      promise: deferredEventPromise.promise,
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    // Start the mutation before synchronous cart subscribers can occupy the main
    // thread. Keep the response handled while the existing feedback gets a turn.
    let cartAddResponse;
    try {
      cartAddResponse = fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      }).then((response) => {
        if (response.status >= 500 || response.status === 408) {
          throw new Error(`Cart request failed: ${response.status}`);
        }
        return response.json();
      });
    } catch (error) {
      cartAddResponse = Promise.reject(error);
    }
    // A fast network failure must not become unhandled while notification yields.
    void cartAddResponse.catch(() => undefined);

    yieldToMainThread()
      .then(() => {
        // A sibling replacement/navigation can detach the source during the yield.
        // Keep the accepted mutation observable by the existing global cart owners.
        const eventTarget = this.isConnected ? this : document;
        eventTarget.dispatchEvent(cartUpdateEvent);
        return cartAddResponse;
      })
      .then(async (response) => {
        if (Number(response.status) >= 500 || Number(response.status) === 408 || (Number(response.status) === 422 && itemCount > 1)) {
          throw new Error(response.message || 'Cart outcome requires verification');
        }
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          // The add failed, so cart state did not change. Consumers already receive
          // CartErrorEvent; reject the pending cart update instead of fetching /cart.json.
          deferredEventPromise.reject(new Error(response.message || 'Add to cart failed'));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        } else {
          cartMutationConfirmed = Boolean(response.id || (Array.isArray(response.items) && response.items.length));
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#refreshCart()
            .then((ajaxCart) => {
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  items: ajaxCart.items,
                  source: 'product-form-component',
                  sourceId: this.id.toString(),
                  itemCount,
                  productId: this.dataset.productId,
                  sections: response.sections,
                  didError: false,
                },
              });

              if (this.#getVariantIdInput()) {
                this.#updateCartQuantity(ajaxCart);
              }

              this.#showConfirmedAddToCartFeedback(
                feedback,
                feedbackProductId,
                feedbackVariantId
              );

              return ajaxCart;
            });
        }
      })
      .catch((error) => {
        console.error(error);
        this.#markCartMutationUncertain();
        deferredEventPromise.reject(error);
        const recoveryMessage = this.#showCartRequestRecovery();

        this.dispatchEvent(
          new CartErrorEvent({
            error: recoveryMessage || error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
            detail: { cartMutationConfirmed },
          })
        );
      })
      .finally(() => {
        if (!feedback?.animationStarted) this.#settleFeedback(feedback);
        this.#activeCartAdds = Math.max(0, this.#activeCartAdds - 1);
        this.#syncCartPendingState();
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * @param {Array<{variantId: string, quantity: number}>} items
   * @param {{ productId?: string | number }} [metadata]
   * @returns {Promise<unknown>}
   */
  #processBatchAddToCart(items, metadata = {}) {
    const feedback = this.#activatePreparedFeedback();
    if (this.#cartMutationUncertain) {
      this.#settleFeedback(feedback);
      this.#syncUncertainCartState();
      return Promise.reject(new Error('Cart mutation requires verification'));
    }
    if (items.length === 0) {
      this.#settleFeedback(feedback);
      return Promise.resolve(false);
    }

    const { addToCartTextError } = this.refs;
    this.refs.cartRequestRecovery?.classList.add('hidden');
    let cartMutationConfirmed = false;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartSectionIds = this.#getCartSectionIds();

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const feedbackProductId = String(metadata.productId ?? this.dataset.productId ?? '');
    const feedbackVariantId = items.length === 1 ? String(items[0]?.variantId || '') : '';
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.#activeCartAdds += 1;
    this.#syncCartPendingState();
    deferredEventPromise.promise.catch(() => {
      for (const container of this.querySelectorAll('add-to-cart-component')) container.resetFeedback();
    });

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.variantId,
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartSectionIds.join(','),
    };

    let cartAddResponse;
    try {
      cartAddResponse = fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      cartAddResponse = Promise.reject(error);
    }

    cartAddResponse
      .then((response) => {
        if (response.status >= 500 || response.status === 408) {
          throw new Error(`Cart request failed: ${response.status}`);
        }
        return response.json();
      })
      .then(async (response) => {
        if (
          Number(response.status) >= 500 || Number(response.status) === 408 ||
          (Number(response.status) === 422 && (items.length > 1 || items.some((item) => item.quantity > 1)))
        ) {
          throw new Error(response.message || 'Cart outcome requires verification');
        }
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          // The batch add failed, so cart state did not change. Avoid a redundant
          // /cart.json request and let CartErrorEvent be the error-state owner.
          deferredEventPromise.reject(new Error(response.message || 'Add to cart failed'));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(response.message));
          }
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        cartMutationConfirmed = Boolean(response.id || (Array.isArray(response.items) && response.items.length));

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-form-component',
            sourceId: this.id.toString(),
            itemCount: totalQuantity,
            productId: metadata.productId ?? this.dataset.productId,
            sections: response.sections,
            didError: false,
          },
        });
        this.#updateCartQuantity(cart);
        this.#showConfirmedAddToCartFeedback(feedback, feedbackProductId, feedbackVariantId);
      })
      .catch((error) => {
        console.error(error);
        this.#markCartMutationUncertain();
        deferredEventPromise.reject(error);
        const recoveryMessage = this.#showCartRequestRecovery();

        this.dispatchEvent(
          new CartErrorEvent({
            error: recoveryMessage || error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
            detail: { cartMutationConfirmed },
          })
        );
      })
      .finally(() => {
        if (!feedback?.animationStarted) this.#settleFeedback(feedback);
        this.#activeCartAdds = Math.max(0, this.#activeCartAdds - 1);
        this.#syncCartPendingState();
      });

    return deferredEventPromise.promise;
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  #showCartRequestRecovery() {
    const recovery = this.refs.cartRequestRecovery;
    if (!recovery || !this.isConnected) return '';

    const message = recovery.textContent?.replace(/\s+/g, ' ').trim() ?? '';
    this.refs.addToCartTextError?.classList.add('hidden');
    recovery.classList.remove('hidden');
    this.#setLiveRegionText(message);
    return message;
  }

  #markCartMutationUncertain() {
    this.#cartMutationUncertain = true;
    this.#addToCartQueue = [];
    this.#settleFeedback(this.#preparedFeedback);
    this.#syncCartPendingState();
    this.#syncUncertainCartState();
  }

  #syncCartPendingState() {
    const pending = this.isAddToCartPending;
    this.toggleAttribute('data-cart-pending', pending);
    for (const container of this.querySelectorAll('add-to-cart-component')) {
      const button = container.refs?.addToCartButton ?? container.querySelector?.('[ref="addToCartButton"]');
      if (pending) button?.setAttribute('aria-busy', 'true');
      else button?.removeAttribute('aria-busy');
    }
    if (!pending) this.#settleFeedback(this.#preparedFeedback);
  }

  #activatePreparedFeedback() {
    const feedback = this.#preparedFeedback;
    this.#preparedFeedback = null;
    this.#activeFeedback = feedback;
    return feedback;
  }

  #settleFeedback(feedback) {
    if (!feedback || feedback.settled) return;
    feedback.settled = true;
    feedback.resolve();
    if (this.#preparedFeedback === feedback) this.#preparedFeedback = null;
    if (this.#activeFeedback === feedback) this.#activeFeedback = null;
  }

  #getCurrentSubmittedVariantId() {
    const form = this.querySelector('form');
    if (!form) return '';
    try {
      return String(new FormData(form).get('id') || '');
    } catch {
      return String(this.refs.variantId?.value || '');
    }
  }

  #showConfirmedAddToCartFeedback(feedback, productId, variantId) {
    if (!feedback || feedback.settled) return;
    const { button } = feedback;
    if (
      !this.isConnected ||
      !button?.isConnected ||
      String(this.dataset.productId || '') !== productId ||
      (variantId && this.#getCurrentSubmittedVariantId() !== variantId)
    ) {
      this.#settleFeedback(feedback);
      return;
    }

    feedback.animationStarted = true;
    try {
      Promise.resolve(this.refs.addToCartButtonContainer?.animateAddToCart?.(button))
        .catch(() => undefined)
        .finally(() => this.#settleFeedback(feedback));
    } catch {
      this.#settleFeedback(feedback);
    }
  }

  #syncUncertainCartState() {
    if (!this.#cartMutationUncertain || !this.isConnected) return;
    this.setAttribute('data-cart-mutation-uncertain', '');
    for (const container of this.querySelectorAll('add-to-cart-component')) container.disable();
    this.#showCartRequestRecovery();
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null, morphFn) {
    if (currentElement && newElement) {
      morphFn(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;
    // Hold the in-flight section-fetch promise so the queue drain can await it before reading the
    // resolved variant id.
    this.#pendingVariantChange = event.promise;

    const localVariantState = event.detail?.localVariantState;
    this.#localVariantState =
      localVariantState?.variantId && typeof localVariantState.available === 'boolean'
        ? { variantId: String(localVariantState.variantId), available: localVariantState.available }
        : null;

    // A new size selection must win visually over any previous add-success animation.
    this.refs.addToCartButtonContainer?.resetFeedback();

    if (this.#localVariantState) {
      if (this.#localVariantState.available) {
        this.refs.variantId.value = this.#localVariantState.variantId;
        this.refs.addToCartButtonContainer?.enable();
        this.#applyLocalPurchasePresentation(true);
      } else {
        this.refs.variantId.value = '';
        this.refs.addToCartButtonContainer?.disable();
        this.#applyLocalPurchasePresentation(false);
      }
    } else {
      this.refs.variantId.value = '';
      this.refs.addToCartButtonContainer?.disable();
    }

    this.setAttribute('aria-busy', 'true');
    this.refs.addToCartTextError?.classList.add('hidden');
    this.#clearLiveRegionText();

    try {
      const { detail } = await event.promise;
      if (generation !== this.#variantChangeGeneration) return;
      if (!detail?.html) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';

      const { addToCartButtonContainer: currentAddToCartButtonContainer } = this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || !currentAddToCartButton) return;

      // Update the button state
      if (resource == null || resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        // Keep the live button/ref stable; only copy server-rendered contents.
        // Variant changes do not require a generic DOM morph for this leaf node.
        currentAddToCartButton.replaceChildren(
          ...Array.from(newAddToCartButton.childNodes, (node) => node.cloneNode(true))
        );
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      const { morph } = await import('@theme/morph');

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback, morph);
        }
      }

      // Morph volume pricing if it exists
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons, morph);

      // Fetch cart state only when this PDP actually renders quantity-dependent UI.
      // Most footwear PDPs do not, so avoid an otherwise redundant /cart.json request
      // after every size change.
      if (
        this.refs.quantitySelector ||
        this.refs.quantityLabelCartCount ||
        this.refs.quantityRules ||
        this.refs.volumePricing
      ) {
        void this.#refreshCart()
          .then((cart) => {
            if (generation === this.#variantChangeGeneration && this.isConnected) this.#updateCartQuantity(cart);
          })
          .catch((error) => {
            if (error?.name !== 'AbortError') console.warn('[product-form] Cart quantity refresh unavailable:', error);
          });
      }
    } catch (error) {
      if (error?.name !== 'AbortError' && generation === this.#variantChangeGeneration) {
        this.refs.variantId.value = '';
        this.refs.addToCartButtonContainer?.disable();
        const message = this.dataset.variantLoadError;
        const { addToCartTextError } = this.refs;
        if (message && addToCartTextError) {
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(message));
          }
          addToCartTextError.classList.remove('hidden');
          this.#setLiveRegionText(message);
        }
      }
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;
        this.removeAttribute('aria-busy');
        this.#syncUncertainCartState();

        // Drain any queued add-to-cart requests that accumulated during the variant change
        await this.#drainAddToCartQueue();
      }
    }
  };

  /**
   * Drains the add-to-cart queue accumulated while a variant change was in flight.
   *
   * Each queued add resolves against the selection and generation that were active when Add was
   * clicked. If no variant resolves, that queued add is aborted so a stale, empty, or maxed
   * variant id is never sent. The add-to-cart button is already disabled for unavailable
   * selections in #onProductSelect, so no further UI change is needed.
   */
  async #drainAddToCartQueue() {
    if (this.#cartMutationUncertain) {
      this.#addToCartQueue = [];
      return;
    }
    if (this.#addToCartQueue.length === 0) return;

    const queuedItems = [...this.#addToCartQueue];
    this.#addToCartQueue = [];
    this.#queuedCartDrain = true;
    this.#syncCartPendingState();

    /** @type {Array<{variantId: string, quantity: number}>} */
    const resolvedItems = [];
    try {
      for (const item of queuedItems) {
        const resolvedItem = await this.#resolveQueuedAddToCartItem(item);
        if (resolvedItem) {
          resolvedItems.push(resolvedItem);
        }
      }
      void this.#processBatchAddToCart(resolvedItems).catch(() => undefined);
    } finally {
      this.#queuedCartDrain = false;
      this.#syncCartPendingState();
    }
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string, quantity: number} | null>}
   */
  async #resolveQueuedAddToCartItem(item) {
    const { variantId, quantityConstraints } = await this.#resolveQueuedVariant(item);
    if (!variantId) return null;

    return {
      variantId,
      quantity: this.#normalizeQueuedQuantity(item.quantity, quantityConstraints),
    };
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string | null, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveQueuedVariant(item) {
    /** @type {string | null} */
    let resolvedVariantId = null;
    /** @type {boolean | undefined} */
    let available;
    /** @type {QuantityConstraints | null} */
    let quantityConstraints = null;

    if (item.pendingVariantChange) {
      try {
        const result = /** @type {{detail?: {resource?: any, html?: Document | Element}}} */ (
          await item.pendingVariantChange
        );
        const resource = result?.detail?.resource;
        quantityConstraints = this.#getQuantityConstraintsFromHtml(result?.detail?.html);
        if (resource === null) {
          available = false;
        } else if (resource) {
          resolvedVariantId = resource.id != null ? String(resource.id) : null;
          available = resource.available !== false;
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          return { variantId: null, quantityConstraints: null };
        }
        if (item.intendedVariantId && item.intendedAvailable !== undefined) {
          // Single-option products already expose the authoritative variant directly
          // on the selected option. Avoid a second section-rendering request when the
          // original request was merely superseded by a newer selection.
          resolvedVariantId = item.intendedVariantId;
          available = item.intendedAvailable;
        } else {
          const resolvedVariant = await this.#resolveVariantFromUrl(item.variantResolutionUrl).catch(() => ({
            variantId: null,
            available: false,
            quantityConstraints: null,
          }));
          resolvedVariantId = resolvedVariant.variantId;
          available = resolvedVariant.available;
          quantityConstraints = resolvedVariant.quantityConstraints;
        }
      }
    }

    const isLatestGeneration = item.generation === this.#variantChangeGeneration;
    const variantId = resolveVariantId({
      resolvedVariantId,
      intendedVariantId: item.intendedVariantId,
      hiddenInputValue: isLatestGeneration ? this.#getVariantIdInput() ?? null : null,
      available: available ?? (isLatestGeneration ? !this.#isAddToCartDisabled() : undefined),
    });

    return { variantId, quantityConstraints };
  }

  /**
   * @param {number} quantity
   * @param {QuantityConstraints | null} quantityConstraints
   * @returns {number}
   */
  #normalizeQueuedQuantity(quantity, quantityConstraints) {
    if (!quantityConstraints) return quantity;

    const min = parseIntOrDefault(quantityConstraints.min, 1);
    const max = parseIntOrDefault(quantityConstraints.max, null);
    const step = parseIntOrDefault(quantityConstraints.step, 1);
    const cartQuantity = parseIntOrDefault(quantityConstraints.cartQuantity, 0);
    const effectiveMax = max === null ? null : Math.max(max - cartQuantity, min);

    let normalizedQuantity = quantity;
    if ((quantity - min) % step !== 0) {
      normalizedQuantity = min + Math.floor((quantity - min) / step) * step;
    }

    return Math.max(min, Math.min(effectiveMax ?? Infinity, normalizedQuantity));
  }

  /**
   * @param {Document | Element | null | undefined} html
   * @returns {QuantityConstraints | null}
   */
  #getQuantityConstraintsFromHtml(html) {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      html?.querySelector?.('quantity-selector-component input[ref="quantityInput"]') ?? null
    );
    if (!quantityInput) return null;

    return {
      min: quantityInput.min,
      max: quantityInput.max || null,
      step: quantityInput.step,
      cartQuantity: quantityInput.getAttribute('data-cart-quantity'),
    };
  }

  /**
   * Resolves a queued selection using the server-side section renderer when the original in-flight
   * request was aborted by a later variant selection.
   * @param {string | null} variantResolutionUrl
   * @returns {Promise<{variantId: string | null, available: boolean | undefined, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveVariantFromUrl(variantResolutionUrl) {
    if (!variantResolutionUrl) return { variantId: null, available: undefined, quantityConstraints: null };

    const response = await fetch(variantResolutionUrl, { credentials: 'same-origin' });
    if (!response.ok) return { variantId: null, available: false, quantityConstraints: null };

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const quantityConstraints = this.#getQuantityConstraintsFromHtml(html);
    const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!textContent) return { variantId: null, available: false, quantityConstraints };

    const resource = JSON.parse(textContent);
    if (!resource || typeof resource !== 'object') return { variantId: null, available: false, quantityConstraints };

    return {
      variantId: resource.id != null ? String(resource.id) : null,
      available: resource.available !== false,
      quantityConstraints,
    };
  }

  /**
   * @returns {import('@theme/variant-picker').default | null}
   */
  #getVariantPicker() {
    const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');
    const pickers = /** @type {import('@theme/variant-picker').default[]} */ (
      Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? [])
    );
    const productId = this.dataset.productId;

    if (productId) {
      const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);
      if (matchingPicker) return matchingPicker;
    }

    return pickers.length === 1 ? pickers[0] ?? null : null;
  }

  /**
   * Whether the current selection's add-to-cart button is disabled (unavailable selection).
   * @returns {boolean}
   */
  #isAddToCartDisabled() {
    const containers = /** @type {NodeListOf<AddToCartComponent>} */ (this.querySelectorAll('add-to-cart-component'));
    return Array.from(containers).some((container) => container.refs.addToCartButton?.disabled);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
